import { useEffect, useRef, useState, useSyncExternalStore } from "react";

import { nextRevealCount, REVEAL_INTERVAL_MS } from "./smoothReveal";

// Render-buffer hook for streamed assistant text (SILL-459). Wraps the pure
// `nextRevealCount` cadence in a timer, gated on streaming state and the user's
// reduced-motion preference. Returns the revealed prefix of `fullText`.
//
// This lives at the RENDER layer on purpose: the chat store / reducer keeps
// accumulating the full text (truth) untouched — history, followups, and render
// cards are unaffected. The hook only decides HOW MUCH of the current message
// is shown, so it drops into every surface (Zustand store or local reducer)
// identically.

const REDUCED_MOTION_QUERY = "(prefers-reduced-motion: reduce)";

function subscribeReducedMotion(onChange: () => void): () => void {
  if (typeof window === "undefined" || !window.matchMedia) return () => {};
  const mql = window.matchMedia(REDUCED_MOTION_QUERY);
  mql.addEventListener("change", onChange);
  return () => mql.removeEventListener("change", onChange);
}

function usePrefersReducedMotion(): boolean {
  return useSyncExternalStore(
    subscribeReducedMotion,
    () =>
      typeof window !== "undefined" &&
      !!window.matchMedia &&
      window.matchMedia(REDUCED_MOTION_QUERY).matches,
    () => false,
  );
}

export interface UseSmoothedTextOptions {
  /** Whether the message this text belongs to is actively streaming. */
  streaming: boolean;
  /**
   * Override for `prefers-reduced-motion`. Omit in app code (the hook detects
   * it via `matchMedia`); pass explicitly in tests.
   */
  reducedMotion?: boolean;
}

export function useSmoothedText(
  fullText: string,
  { streaming, reducedMotion }: UseSmoothedTextOptions,
): string {
  const systemReducedMotion = usePrefersReducedMotion();
  const reduced = reducedMotion ?? systemReducedMotion;

  // Character count currently revealed. When a message mounts already-complete
  // (loaded history / a finalized bubble that never streamed here), start fully
  // revealed so old messages never re-type.
  const [count, setCount] = useState<number>(() =>
    streaming ? 0 : fullText.length,
  );
  const countRef = useRef(count);
  const fullRef = useRef(fullText);
  fullRef.current = fullText;
  // Latches true the first time this message streams, so the tail keeps
  // revealing after `streaming` flips false at end_turn (no snap-truncate).
  const hasStreamedRef = useRef(streaming);

  useEffect(() => {
    if (streaming) hasStreamedRef.current = true;
    const targetLen = fullRef.current.length;

    // Target shrank (a new turn reset the accumulator to "" through the same
    // hook instance — the onboarding intake case). Clamp down so the fresh
    // turn re-smooths from its start instead of dumping instantly.
    if (countRef.current > targetLen) {
      countRef.current = targetLen;
      setCount(targetLen);
    }

    // Instant paths: reduced motion, or a message that has never streamed in
    // this mount. Reveal everything, no animation.
    if (reduced || !hasStreamedRef.current) {
      if (countRef.current !== targetLen) {
        countRef.current = targetLen;
        setCount(targetLen);
      }
      return;
    }

    // Already caught up — wait for the next `fullText` change to re-run.
    if (countRef.current >= targetLen) return;

    const id = setInterval(() => {
      const next = nextRevealCount(countRef.current, fullRef.current);
      countRef.current = next;
      setCount(next);
      if (next >= fullRef.current.length) clearInterval(id);
    }, REVEAL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [fullText, reduced, streaming]);

  // Reduced motion: always the full text, no timing dependency.
  if (reduced) return fullText;

  // While streaming, never emit an empty frame when text exists: surfaces flip
  // their typing indicator off the instant raw text arrives, so a "" frame here
  // would blank the bubble for a tick (dots → empty box → text). Show at least
  // the first character immediately; the interval takes over from there. Also
  // guards against `count` briefly trailing a replacement `fullText`.
  const floor = streaming && fullText.length > 0 ? 1 : 0;
  const shown = Math.min(fullText.length, Math.max(count, floor));
  return fullText.slice(0, shown);
}
